/**
 * JourneyStageContent — content overlay that lives on top of the slab.
 *
 * Rendered as plain DOM using drei's <Html> so we get real text rendering
 * (not textures in the 3D scene). The DOM sits centered on the vessel's
 * screen-space position and tracks it as the slab moves — but in this
 * prototype the slab is stationary while content is shown, so tracking
 * is only meaningful during solidifying / softening.
 *
 * The three business stages share one shape: header · body · footer, all
 * gently faded. This is *not* a checklist — it is a rendering of where
 * the guest is right now.
 *
 * Content-level fade is separate from the physical fade of the slab.
 * The slab can be solid while content is still fading in.
 */
import React from "react";
import { Html } from "@react-three/drei";
import { motion, AnimatePresence } from "framer-motion";

// Sample guest — this is a *prototype*, not a live data binding.
const SAMPLE_GUEST = {
  name: "Rowan Ashe",
  ritual: "Now Massage · 60 min",
  therapist: "Maren V.",
  room: "3",
  first_visit: false,
};

const STAGE_BODIES = {
  arrival: {
    eyebrow: "Arrival",
    lead: "Rowan is here.",
    supporting:
      "Confirm ritual, introduce enhancements, hand off to Maren.",
    footer_left: "Room 3 · 4:00 pm",
    footer_right: "Now Massage · 60 min",
  },
  in_journey: {
    eyebrow: "In Journey",
    lead: "Rowan is in ritual.",
    supporting:
      "Maren is with them. Nothing is waiting on you right now.",
    footer_left: "Room 3 · began 4:04 pm",
    footer_right: "Now Massage · 60 min",
  },
  departure: {
    eyebrow: "Departure",
    lead: "Rowan is ready.",
    supporting:
      "Review the experience. Then, if it went well, invite a Google review, introduce the Ritual Program, and offer rebook.",
    footer_left: "Room 3 · ended 5:04 pm",
    footer_right: "Next: Review",
  },
};

export default function JourneyStageContent({ visible, stage, guest = SAMPLE_GUEST, position }) {
  return (
    <Html
      position={position}
      center
      distanceFactor={4.6}
      pointerEvents="none"
      transform={false}
      style={{
        userSelect: "none",
        pointerEvents: "none",
      }}
    >
      <div
        data-testid="stage-content-wrap"
        style={{
          width: 260,
          transform: "translate(0, 0)",
          transition: "opacity 400ms ease-out",
          opacity: visible ? 1 : 0,
        }}
      >
        <AnimatePresence mode="wait">
          {visible && (
            <motion.div
              key={stage}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
              style={{
                textAlign: "center",
                color: "#1c1917",
              }}
              data-testid={`stage-content-${stage}`}
            >
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "rgba(28,25,23,0.55)",
                  marginBottom: 8,
                }}
              >
                {STAGE_BODIES[stage].eyebrow}
              </div>
              <div
                style={{
                  fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
                  fontWeight: 400,
                  fontSize: 32,
                  lineHeight: 1.15,
                  letterSpacing: "-0.005em",
                  marginBottom: 10,
                }}
              >
                {STAGE_BODIES[stage].lead}
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: "rgba(28,25,23,0.65)",
                  lineHeight: 1.55,
                  maxWidth: 260,
                  margin: "0 auto 18px",
                }}
              >
                {STAGE_BODIES[stage].supporting}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 10,
                  letterSpacing: "0.05em",
                  color: "rgba(28,25,23,0.45)",
                  paddingTop: 10,
                  borderTop: "1px solid rgba(28,25,23,0.08)",
                }}
              >
                <span>{STAGE_BODIES[stage].footer_left}</span>
                <span>{STAGE_BODIES[stage].footer_right}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Html>
  );
}
